/**
 * Visualizador do cupom NFC-e (HTML retornado pela Focus via proxy autenticado).
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { Receipt, AlertCircle, Loader2, Download } from 'lucide-react'
import ModalERP from '../../ModalERP'
import api from '../../../../services/api'
import { exportNfceHtmlToPdf } from '../../../../utils/exportNfceHtmlToPdf'
import './NfceViewerModal.css'

function getNfceProxyUrl(saleId) {
  return `/api/v1/fiscal/nfce/${saleId}/danfe-proxy`
}

function NfceViewerModal({ isOpen, onClose, saleId, saleNumber }) {
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [downloadError, setDownloadError] = useState(null)
  const [error, setError] = useState(null)
  const [htmlContent, setHtmlContent] = useState(null)
  const iframeRef = useRef(null)

  const title = saleNumber ? `NFC-e — Venda #${saleNumber}` : 'Cupom NFC-e'

  const parseErrorMessage = async (err) => {
    const data = err.response?.data
    if (typeof data === 'string') {
      try {
        const json = JSON.parse(data)
        if (json?.message) return json.message
      } catch {
        if (data.trim()) return data
      }
    }
    if (data?.message) return data.message
    if (err.response?.status === 401) {
      return 'Erro de autenticação. Faça login novamente.'
    }
    return 'Erro ao carregar o cupom NFC-e.'
  }

  const loadCupom = useCallback(async () => {
    if (!saleId) return

    setLoading(true)
    setError(null)
    setHtmlContent(null)

    try {
      const response = await api.get(getNfceProxyUrl(saleId), {
        responseType: 'text',
        headers: { Accept: 'text/html, */*' },
        transformResponse: [(data) => data]
      })

      const contentType = (response.headers?.['content-type'] || '').toLowerCase()
      if (contentType.includes('json')) {
        const json = JSON.parse(response.data)
        throw new Error(json.message || 'Cupom NFC-e não disponível')
      }

      const html = String(response.data || '').trim()
      if (!html || html.length < 20) {
        throw new Error('Conteúdo do cupom NFC-e vazio ou inválido')
      }
      if (html.startsWith('{') || html.startsWith('[')) {
        throw new Error(
          'O servidor retornou dados incorretos. Reinicie o backend e tente novamente.'
        )
      }
      if (!html.startsWith('<')) {
        throw new Error('Formato do cupom NFC-e não reconhecido')
      }

      setHtmlContent(html)
      setLoading(false)
    } catch (err) {
      const message = err.message && !err.response ? err.message : await parseErrorMessage(err)
      setError(message)
      setLoading(false)
    }
  }, [saleId])

  useEffect(() => {
    if (!isOpen || !saleId) return
    loadCupom()
  }, [isOpen, saleId, loadCupom])

  useEffect(() => {
    if (!isOpen) {
      setHtmlContent(null)
      setError(null)
      setLoading(true)
    }
  }, [isOpen])

  const handleClose = () => {
    setHtmlContent(null)
    setError(null)
    setLoading(true)
    setDownloading(false)
    setDownloadError(null)
    onClose()
  }

  const handleDownloadPdf = async () => {
    if (downloading) return

    // Preferir o iframe visível: scripts do cupom Focus (QR Code) já executaram.
    const iframeBody = iframeRef.current?.contentDocument?.body
    const source = iframeBody || htmlContent

    if (!source) {
      setDownloadError('Aguarde o cupom carregar antes de baixar o PDF.')
      return
    }

    setDownloading(true)
    setDownloadError(null)
    try {
      await exportNfceHtmlToPdf(
        source,
        `NFC-e-venda-${saleNumber || saleId}.pdf`
      )
    } catch (err) {
      setDownloadError(err.message || 'Erro ao gerar PDF do cupom NFC-e.')
    } finally {
      setDownloading(false)
    }
  }

  return (
    <ModalERP
      isOpen={isOpen}
      onClose={handleClose}
      title={title}
      subtitle="Cupom Fiscal Eletrônico (NFC-e)"
      icon={<Receipt size={22} />}
      size="lg"
      footer={
        <div className="nfce-viewer-footer">
          <button
            type="button"
            className="modal-erp-btn-primary nfce-viewer-download-btn"
            onClick={handleDownloadPdf}
            disabled={!htmlContent || loading || downloading}
          >
            {downloading ? (
              <>
                <Loader2 size={16} className="modal-erp-spinner" />
                Baixando PDF...
              </>
            ) : (
              <>
                <Download size={16} />
                Baixar PDF
              </>
            )}
          </button>
          <button type="button" className="modal-erp-btn-secondary" onClick={handleClose}>
            Fechar
          </button>
        </div>
      }
    >
      {downloadError && (
        <p className="nfce-viewer-download-error" role="alert">
          {downloadError}
        </p>
      )}
      <div className="nfce-viewer-body">
        {loading && (
          <div className="nfce-viewer-state">
            <Loader2 size={32} className="modal-erp-spinner" />
            <p>Carregando cupom NFC-e...</p>
          </div>
        )}
        {error && (
          <div className="nfce-viewer-state nfce-viewer-error">
            <AlertCircle size={28} />
            <p>{error}</p>
            <button type="button" className="modal-erp-btn-secondary" onClick={loadCupom}>
              Tentar novamente
            </button>
          </div>
        )}
        {htmlContent && !error && (
          <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            className="nfce-viewer-iframe"
            title={title}
            onLoad={() => setLoading(false)}
            style={{ display: loading ? 'none' : 'block' }}
          />
        )}
      </div>
    </ModalERP>
  )
}

export default NfceViewerModal
